import React from 'react';
import Head from 'next/head';
import Link from 'next/link';

export default function MaterialExecutivo() {
  const [formSubmitting, setFormSubmitting] = React.useState(false);
  const [formSubmitted, setFormSubmitted] = React.useState(false);

  const applyPhoneMask = (value) => {
    if (!value) return '';
    value = value.replace(/\D/g, '');
    if (value.length <= 2) return value;
    if (value.length <= 7) return `(${value.slice(0, 2)}) ${value.slice(2)}`;
    return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
  };

  const handlePhoneInput = (e) => {
    const input = e.target;
    input.value = applyPhoneMask(input.value);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);

    const formData = new FormData(e.target);
    const formValues = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formValues,
          origem: 'material-executivo',
        })
      });

      if (response.ok) {
        setFormSubmitted(true);
        e.target.reset();
      } else {
        alert("Ocorreu um erro ao enviar. Por favor, tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao enviar:", error);
      alert("Ocorreu um erro ao enviar. Por favor, tente novamente.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('formulario-material')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Head>
        <title>Material Executivo | O papel do app nativo no crescimento do e-commerce</title>
        <meta name="description" content="Relatório estratégico sobre como o app nativo está se tornando canal central de retenção, recorrência e crescimento no varejo digital. Baseado em dados reais de mercado." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="icon" href="/images/icone.png" />
        <meta property="og:title" content="Material Executivo | App nativo no crescimento do e-commerce" />
        <meta property="og:description" content="Relatório estratégico com dados de mercado sobre retenção, recorrência e crescimento via canal mobile proprietário." />
        <meta property="og:type" content="website" />
      </Head>

      {/* Header simplificado para landing */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-8">
          <Link href="/" className="flex items-center">
            <img src="/images/logo.svg" alt="E-Commerce App" className="h-10 w-auto" width="120" height="40" />
          </Link>
          <button
            onClick={scrollToForm}
            className="rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-emerald-800"
          >
            Acessar material
          </button>
        </div>
      </header>

      <main className="relative min-h-screen">

        {/* ============================================= */}
        {/* 1. HERO — IMPACTO / PARAR A ROLAGEM           */}
        {/* ============================================= */}
        <section className="relative bg-white overflow-hidden">
          {/* Subtle decorative element */}
          <div className="absolute top-0 right-0 w-[40%] h-full bg-gradient-to-l from-emerald-50/60 to-transparent pointer-events-none"></div>

          <div className="mx-auto max-w-7xl px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-80px)] py-16 lg:py-0">

              {/* Left — Copy que provoca */}
              <div className="max-w-xl">
                <div className="flex items-center gap-3 mb-10">
                  <span className="bg-emerald-900 text-white text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full">
                    Relatório executivo
                  </span>
                  <span className="text-sm text-gray-400 tracking-wide">Acesso gratuito</span>
                </div>

                <h1 className="text-4xl lg:text-5xl xl:text-[3.5rem] font-light leading-[1.12] text-zinc-900 mb-8">
                  O app deixou de ser acessório. Em muitas operações, ele já virou{' '}
                  <span className="font-semibold italic">canal central de receita digital.</span>
                </h1>

                <p className="text-lg lg:text-xl text-gray-500 leading-relaxed mb-10">
                  Um material com dados de mercado, comportamento do cliente multicanal e o peso real do app na receita digital das operações mais maduras do varejo.
                </p>

                <button
                  onClick={scrollToForm}
                  className="bg-lime-400 hover:bg-lime-300 text-emerald-900 font-semibold px-8 py-4 rounded-full text-base transition-all duration-200 inline-flex items-center group"
                >
                  Acessar material executivo
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                </button>

                <div className="flex items-center gap-8 mt-12">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-extrabold text-emerald-900 tracking-tight">81%</span>
                    <span className="text-sm text-gray-500">receita via app</span>
                  </div>
                  <div className="w-px h-8 bg-gray-200"></div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-extrabold text-emerald-900 tracking-tight">51M</span>
                    <span className="text-sm text-gray-500">clientes analisados</span>
                  </div>
                  <div className="w-px h-8 bg-gray-200"></div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-extrabold text-emerald-900 tracking-tight">21x</span>
                    <span className="text-sm text-gray-500">compras/ano multicanal</span>
                  </div>
                </div>
              </div>

              {/* Right — Mockup visual */}
              <div className="hidden lg:flex justify-end items-center">
                <div className="relative">
                  <div className="absolute -inset-4 bg-emerald-100/40 rounded-[2.5rem] -rotate-2"></div>
                  <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100/80 overflow-hidden max-w-sm">
                    {/* Selo de páginas */}
                    <div className="absolute top-4 right-4 z-10 bg-white/90 backdrop-blur-sm text-emerald-900 text-xs font-bold px-3 py-1.5 rounded-full shadow-sm">
                      Material executivo
                    </div>
                    {/* Header visual do PDF — capa */}
                    <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 px-10 pt-14 pb-8 text-center">
                      <div className="w-14 h-14 bg-lime-400 rounded-2xl mx-auto mb-5 flex items-center justify-center">
                        <svg className="w-7 h-7 text-emerald-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      </div>
                      <p className="text-white font-semibold text-lg leading-snug mb-1">O papel do app nativo<br/>no crescimento do<br/>e-commerce</p>
                      <p className="text-emerald-300 text-xs tracking-wider uppercase mt-3">E-Commerce App — 2025</p>
                    </div>
                    {/* Stat highlight */}
                    <div className="bg-emerald-50 px-10 py-5 border-b border-emerald-100">
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-4xl font-bold text-emerald-900">81%</span>
                        <span className="text-sm text-emerald-700 leading-tight text-left">da receita digital<br/>já vem do app</span>
                      </div>
                    </div>
                    {/* Preview content lines */}
                    <div className="p-8 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-1.5">
                          <div className="h-2 bg-gray-200 rounded-full w-full"></div>
                          <div className="h-2 bg-gray-100 rounded-full w-3/4"></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-lime-100 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-1.5">
                          <div className="h-2 bg-gray-200 rounded-full w-5/6"></div>
                          <div className="h-2 bg-gray-100 rounded-full w-2/3"></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-1.5">
                          <div className="h-2 bg-gray-200 rounded-full w-full"></div>
                          <div className="h-2 bg-gray-100 rounded-full w-1/2"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>


        {/* ============================================= */}
        {/* 2. TENSÃO — O PROBLEMA QUE NINGUÉM FALA       */}
        {/* ============================================= */}
        <section className="py-20 lg:py-28 bg-gray-50">
          <div className="mx-auto max-w-4xl px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="inline-block text-emerald-600 text-sm font-semibold tracking-wider uppercase mb-4">O cenário mudou</span>
              <h2 className="text-3xl lg:text-[2.6rem] font-light text-zinc-900 leading-snug">
                Crescer no digital ficou mais caro.<br/>
                <span className="font-semibold">Reter continua sendo o maior desafio.</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>
                </div>
                <p className="text-zinc-800 font-medium mb-1">O custo de aquisição sobe todo trimestre.</p>
                <p className="text-gray-500 text-sm leading-relaxed">Mais concorrentes no leilão. Mesmo orçamento, menos resultados.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <p className="text-zinc-800 font-medium mb-1">Vender uma vez não constrói recorrência.</p>
                <p className="text-gray-500 text-sm leading-relaxed">Sem canal direto, cada venda depende de um novo investimento em mídia.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                </div>
                <p className="text-zinc-800 font-medium mb-1">Depender só do site enfraquece o relacionamento.</p>
                <p className="text-gray-500 text-sm leading-relaxed">O cliente não volta espontaneamente. E o e-mail já não tem a mesma força.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-sm">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                </div>
                <p className="text-zinc-800 font-medium mb-1">Quem constrói canal direto cresce com mais controle.</p>
                <p className="text-gray-500 text-sm leading-relaxed">Marcas com audiência própria dependem menos de terceiros para vender.</p>
              </div>
            </div>

            <p className="text-center text-gray-400 text-sm mt-10">
              O material aborda esse cenário com dados e contexto — sem simplificações.
            </p>
          </div>
        </section>


        {/* ============================================= */}
        {/* 3. PROVA — DADOS QUE ABREM CURIOSIDADE        */}
        {/* ============================================= */}
        <section className="py-20 lg:py-28 bg-emerald-900 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-lime-400/8 rounded-full -translate-y-1/3 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-lime-400/8 rounded-full translate-y-1/3 -translate-x-1/3"></div>

          <div className="mx-auto max-w-6xl px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <span className="inline-block bg-lime-400 text-emerald-900 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
                Prévia do material
              </span>
              <h2 className="text-3xl lg:text-[2.6rem] font-light text-white leading-snug">
                Alguns dos números que você vai<br/>
                <span className="font-semibold">encontrar neste relatório</span>
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Anchor number 1 */}
              <div className="bg-lime-400/15 backdrop-blur-sm rounded-2xl p-8 text-center border border-lime-400/30 hover:bg-lime-400/20 transition-colors ring-1 ring-lime-400/20">
                <div className="text-6xl font-extrabold text-lime-400 mb-3 tracking-tight">81%</div>
                <p className="text-white text-sm font-medium leading-relaxed">da receita digital via app em operações de referência</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10 hover:bg-white/15 transition-colors">
                <div className="text-5xl font-bold text-lime-400 mb-3">51M</div>
                <p className="text-emerald-100 text-sm leading-relaxed">de clientes ativos em ecossistemas digitais analisados</p>
              </div>
              {/* Anchor number 2 */}
              <div className="bg-lime-400/15 backdrop-blur-sm rounded-2xl p-8 text-center border border-lime-400/30 hover:bg-lime-400/20 transition-colors ring-1 ring-lime-400/20">
                <div className="text-6xl font-extrabold text-lime-400 mb-3 tracking-tight">21x</div>
                <p className="text-white text-sm font-medium leading-relaxed">compras por ano do cliente multicanal em operações maduras</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/10 hover:bg-white/15 transition-colors">
                <div className="text-5xl font-bold text-lime-400 mb-3">97%</div>
                <p className="text-emerald-100 text-sm leading-relaxed">das entregas/coletas em até 60 min nos cases analisados</p>
              </div>
            </div>

            <p className="text-center text-emerald-300/70 text-sm mt-10">
              Esses números têm um contexto. O material mostra o que está por trás deles.
            </p>

            <div className="text-center mt-8">
              <button
                onClick={scrollToForm}
                className="bg-lime-400 hover:bg-lime-300 text-emerald-900 font-semibold px-8 py-4 rounded-full text-base transition-all duration-200 inline-flex items-center group"
              >
                Quero entender o contexto
                <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
              </button>
            </div>
          </div>
        </section>


        {/* ============================================= */}
        {/* 4. INTERPRETAÇÃO — O QUE ISSO SIGNIFICA        */}
        {/* ============================================= */}
        <section className="py-20 lg:py-28 bg-white">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">
            <div className="max-w-3xl mx-auto mb-16">
              <span className="inline-block text-emerald-600 text-sm font-semibold tracking-wider uppercase mb-4">Leitura estratégica</span>
              <h2 className="text-3xl lg:text-[2.6rem] font-light text-zinc-900 leading-snug mb-6">
                O ponto não é apenas <span className="font-semibold">vender pelo app.</span>
              </h2>
              <p className="text-lg text-gray-500 leading-relaxed">
                O ponto é usar o app como canal de conveniência, recorrência, dados e relacionamento. Quem entendeu isso primeiro, já está colhendo resultados.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl p-8 bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-lime-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                  </div>
                  <h3 className="text-base font-bold text-zinc-900">Mídia paga é combustível, não motor</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Ela traz o cliente uma vez. Mas quem não tem um canal para manter esse cliente por perto, paga de novo para falar com ele.
                </p>
              </div>

              <div className="rounded-2xl p-8 bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-lime-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </div>
                  <h3 className="text-base font-bold text-zinc-900">Recompra é o novo crescimento</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">
                  As operações mais sólidas do varejo digital crescem fazendo o mesmo cliente comprar mais vezes. O app é a infraestrutura que viabiliza isso.
                </p>
              </div>

              <div className="rounded-2xl p-8 bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-lime-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                  </div>
                  <h3 className="text-base font-bold text-zinc-900">O app funciona como CRM no bolso do cliente</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Push, fidelidade, checkout salvo, experiência fluida. Tudo o que um CRM precisa para funcionar — dentro de um canal que a pessoa carrega o dia inteiro.
                </p>
              </div>

              <div className="rounded-2xl p-8 bg-gray-50 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-900 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-lime-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  </div>
                  <h3 className="text-base font-bold text-zinc-900">O cliente volta por conveniência, não por desconto</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Operações maduras constroem canais onde o retorno é natural. Menos fricção, mais frequência. Esse é o papel do mobile.
                </p>
              </div>
            </div>
          </div>
        </section>


        {/* ============================================= */}
        {/* 5. VALOR DO MATERIAL — O QUE VOCÊ VAI VER     */}
        {/* ============================================= */}
        <section className="py-20 lg:py-28 bg-gray-50">
          <div className="mx-auto max-w-6xl px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block bg-lime-400 text-emerald-900 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
                Conteúdo do relatório
              </span>
              <h2 className="text-3xl lg:text-[2.6rem] font-light text-zinc-900 leading-snug">
                O que você vai encontrar<br/>
                <span className="font-semibold">dentro do material</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-emerald-200 transition-colors">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">Como o app ganhou protagonismo no digital</h3>
                <p className="text-gray-500 text-sm leading-relaxed">De canal complementar a canal central de receita — o que mudou e por quê.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-200 transition-colors">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">O que os dados revelam sobre o cliente multicanal</h3>
                <p className="text-gray-500 text-sm leading-relaxed">Frequência, ticket e valor no tempo — a diferença é maior do que parece.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-200 transition-colors">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">Por que retenção ficou tão importante</h3>
                <p className="text-gray-500 text-sm leading-relaxed">O custo de conquistar é alto. O custo de manter é cada vez mais estratégico.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-purple-200 transition-colors">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">O papel da conveniência e da omnicanalidade</h3>
                <p className="text-gray-500 text-sm leading-relaxed">A compra acontece onde é mais fácil. E o app é o canal com menor fricção.</p>
              </div>

              <div className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow border border-gray-100 group">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-rose-200 transition-colors">
                  <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                </div>
                <h3 className="font-bold text-zinc-900 mb-2">O que varejistas podem aprender com esse movimento</h3>
                <p className="text-gray-500 text-sm leading-relaxed">Lições práticas de operações que já passaram por essa transição.</p>
              </div>

              {/* Card destaque */}
              <div className="bg-emerald-900 rounded-2xl p-7 text-white relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-lime-400/15 rounded-full -translate-y-1/3 translate-x-1/3"></div>
                <div className="relative">
                  <div className="w-12 h-12 bg-lime-400 rounded-xl flex items-center justify-center mb-5">
                    <svg className="w-6 h-6 text-emerald-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <h3 className="font-bold text-white mb-2">Tudo organizado em um relatório único</h3>
                  <p className="text-emerald-200 text-sm leading-relaxed">Leitura rápida, direto ao ponto, pensado para quem toma decisão.</p>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* ============================================= */}
        {/* 6. FORMULÁRIO — CONVERSÃO                      */}
        {/* ============================================= */}
        <section id="formulario-material" className="py-20 lg:py-28 bg-white">
          <div className="mx-auto max-w-5xl px-6 lg:px-8">

            {formSubmitted ? (
              <div className="relative bg-emerald-900 rounded-3xl px-10 lg:px-16 py-20 overflow-hidden text-center">
                <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400 rounded-bl-[4rem]"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-lime-400 rounded-tr-[4rem]"></div>
                <div className="relative z-10">
                  <div className="w-20 h-20 bg-lime-400 rounded-full mx-auto flex items-center justify-center mb-6">
                    <svg className="w-10 h-10 text-emerald-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h2 className="text-3xl font-light text-white mb-4">Material a caminho</h2>
                  <p className="text-emerald-200 max-w-md mx-auto mb-8">
                    Você receberá o relatório no e-mail informado em instantes.
                  </p>
                  <Link href="/" className="bg-lime-400 hover:bg-lime-300 text-emerald-900 font-semibold px-8 py-4 rounded-full text-base transition-colors inline-block">
                    Voltar ao site
                  </Link>
                </div>
              </div>
            ) : (
              <div className="relative bg-emerald-900 rounded-3xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400 rounded-bl-[4rem]"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-lime-400 rounded-tr-[4rem]"></div>

                <div className="relative z-10 px-8 lg:px-14 py-16 lg:py-20">
                  <div className="text-center mb-10">
                    <h2 className="text-3xl lg:text-4xl font-light text-white mb-3">
                      Acesse o relatório completo
                    </h2>
                    <p className="text-emerald-200/80 max-w-md mx-auto text-sm">
                      Preencha abaixo. Enviaremos o PDF diretamente no seu e-mail.
                    </p>
                  </div>

                  <form onSubmit={handleFormSubmit} className="max-w-3xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input type="text" name="nome" placeholder="Nome completo" required disabled={formSubmitting} className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800 placeholder-gray-400" />
                      <input type="text" name="empresa" placeholder="Empresa" required disabled={formSubmitting} className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800 placeholder-gray-400" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input type="text" name="cargo" placeholder="Cargo" required disabled={formSubmitting} className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800 placeholder-gray-400" />
                      <input type="email" name="email" placeholder="E-mail corporativo" required disabled={formSubmitting} className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800 placeholder-gray-400" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <input type="tel" name="whatsapp" placeholder="WhatsApp" required disabled={formSubmitting} onInput={handlePhoneInput} className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800 placeholder-gray-400" />
                      <select name="segmento" required disabled={formSubmitting} defaultValue="" className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800">
                        <option value="" disabled>Segmento</option>
                        <option value="Farmácia">Farmácia</option>
                        <option value="Supermercado">Supermercado</option>
                        <option value="Petshop">Petshop</option>
                        <option value="Moda e vestuário">Moda e vestuário</option>
                        <option value="Saúde e beleza">Saúde e beleza</option>
                        <option value="Alimentação">Alimentação</option>
                        <option value="Outro varejo">Outro varejo</option>
                      </select>
                    </div>

                    <div className="mb-6">
                      <select name="faturamento" disabled={formSubmitting} defaultValue="" className="w-full px-5 py-4 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-400 bg-white text-gray-800">
                        <option value="" disabled>Faturamento mensal do e-commerce (opcional)</option>
                        <option value="Até R$ 50 mil">Até R$ 50 mil</option>
                        <option value="R$ 50 mil a R$ 200 mil">R$ 50 mil a R$ 200 mil</option>
                        <option value="R$ 200 mil a R$ 500 mil">R$ 200 mil a R$ 500 mil</option>
                        <option value="R$ 500 mil a R$ 1 milhão">R$ 500 mil a R$ 1 milhão</option>
                        <option value="Acima de R$ 1 milhão">Acima de R$ 1 milhão</option>
                      </select>
                    </div>

                    <div className="text-center">
                      <button
                        type="submit"
                        disabled={formSubmitting}
                        className={`${formSubmitting ? 'bg-gray-300 cursor-not-allowed' : 'bg-lime-400 hover:bg-lime-300'} text-emerald-900 font-semibold px-10 py-4 rounded-full text-base transition-all duration-200 inline-flex items-center group`}
                      >
                        {formSubmitting ? 'Enviando...' : 'Receber o material'}
                        {!formSubmitting && (
                          <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
                        )}
                      </button>
                      <p className="text-emerald-300/50 text-xs mt-4">Sem spam. Apenas o material que você pediu.</p>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </section>


        {/* ============================================= */}
        {/* 7. FECHAMENTO — REFORÇO DE VALOR               */}
        {/* ============================================= */}
        <section className="py-20 lg:py-24 bg-gray-50">
          <div className="mx-auto max-w-3xl px-6 lg:px-8 text-center">
            <p className="text-emerald-600 text-sm font-semibold tracking-wider uppercase mb-4">Para decisores</p>
            <h2 className="text-3xl lg:text-[2.6rem] font-light text-zinc-900 leading-snug mb-6">
              As operações que entenderam o papel do app{' '}
              <span className="font-semibold">já estão jogando outro jogo.</span>
            </h2>
            <p className="text-gray-500 leading-relaxed mb-4">
              Esse material reúne dados, contexto e leitura estratégica para quem quer tomar decisões mais inteligentes sobre retenção, canal próprio e crescimento digital.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mb-10">
              Não é um catálogo de produto. É o tipo de conteúdo que deveria chegar antes da próxima reunião de planejamento.
            </p>
            <button
              onClick={scrollToForm}
              className="bg-emerald-900 hover:bg-emerald-800 text-white font-semibold px-10 py-4 rounded-full text-base transition-all duration-200 inline-flex items-center group"
            >
              Acessar o relatório
              <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/></svg>
            </button>
          </div>
        </section>


        {/* Footer */}
        <footer className="py-12 bg-white border-t border-gray-100">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-8">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Link href="/" className="px-4 py-2 text-sm text-zinc-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">Início</Link>
                <a href="#formulario-material" onClick={(e) => { e.preventDefault(); scrollToForm(); }} className="px-4 py-2 text-sm text-zinc-700 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors">Receber material</a>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mb-8">
              <span className="text-xs text-gray-400 uppercase tracking-wider">POWER BY ECOMMERCEAPP</span>
              <a href="#privacy" className="text-xs text-gray-400 uppercase tracking-wider hover:text-gray-600">POLITICA DE PRIVACIDADE</a>
            </div>
            <div className="text-center">
              <span className="text-6xl lg:text-8xl xl:text-9xl font-bold text-emerald-900 tracking-tight">
                E-Commerce App
              </span>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}
